import React, { useEffect, useState, useContext } from 'react';
import { isNull, isUndefined } from 'lodash';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import AutoSubmitDialog from 'chameleon/packages/blue-book-player/library/AutoSubmitDialog';
import {
  NOT_OPENED_ASSIGNMENT_ERROR_MESSAGE,
  SUBMITTED_BY_TEACHER_ERROR_MESSAGE,
} from 'chameleon/packages/blue-book-player/constants/assignments';
import {
  learnositySubmit,
  learnosityExitPlayer,
} from 'chameleon/packages/blue-book-player/utility/learnosityUtils/submit';
import { SET_SUBMISSION_READY } from 'chameleon/packages/blue-book-player/state/reducer';
import { BluebookContext } from 'chameleon/packages/blue-book-player/state/BluebookContext';
import { withAssignmentSubmit } from 'chameleon/packages/blue-book-player/hocs/player';
import withSessionOnStudentAccountOnly from 'chameleon/packages/blue-book-player/hocs/withSessionOnStudentAccountOnly';
import Countdown from './Countdown';

const CHECK_TIME_EXTENSION_INTERVAL = 30000;
const TIME_ELAPSED_GAP = CHECK_TIME_EXTENSION_INTERVAL / 100 + 10;

const AssignmentSessionWrapper = (props) => {
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(false);
  const {
    dispatch,
    state: { isAutoSubmittedByTimer, isQuizSubmitted },
  } = useContext(BluebookContext);

  const {
    assignmentError,
    userData,
    assignmentPlayer,
    params,
    assignmentId,
    isPreviewPlayer,
    quizTitle,
    timerInfo,
    checkTimeExtension,
    learnosityApp,
    router,
    location: { state },
    params: { subject },
    submitAssignment,
  } = props;

  const { isImpersonating } = userData;

  const submissionReadyCallback = (submissionReady) => {
    dispatch({ type: SET_SUBMISSION_READY, payload: submissionReady });
  };

  /**
   * Add checkTimeExtension for not timed assignments as well.
   * For timed one the API is called in the timer component.
   * */
  useEffect(() => {
    const getInterval = () =>
      setInterval(() => {
        const { timeElapsed, totalTime } =
          timerInfo || assignmentPlayer?.timedSession || {};

        if (timeElapsed + TIME_ELAPSED_GAP >= totalTime || timeElapsed === 0) {
          checkTimeExtension();
        }
      }, CHECK_TIME_EXTENSION_INTERVAL);

    let assignmentSessionInterval = getInterval();

    return () => {
      clearInterval(assignmentSessionInterval);
    };
  }, [timerInfo, assignmentPlayer]);

  useEffect(() => {
    const hasError = assignmentError?.hasError;
    const errorMessage = assignmentError?.message;
    const notOpenedAssignmentError =
      errorMessage &&
      errorMessage.endsWith(NOT_OPENED_ASSIGNMENT_ERROR_MESSAGE);
    const submittedByTeacherError =
      errorMessage && errorMessage.endsWith(SUBMITTED_BY_TEACHER_ERROR_MESSAGE);
    setShouldAutoSubmit(
      !isAutoSubmittedByTimer &&
        hasError &&
        (notOpenedAssignmentError || submittedByTeacherError) &&
        !isQuizSubmitted,
    );
  }, [assignmentError, isAutoSubmittedByTimer]);

  useEffect(() => {
    // Submit the assignment whenever timer has a hard limit and it expired
    // And instead of `Auto Submission` modal we show `Time is Up` modal
    if ((shouldAutoSubmit || isAutoSubmittedByTimer) && !isImpersonating) {
      learnositySubmit(learnosityApp, submissionReadyCallback, isImpersonating);
      submitAssignment(assignmentId);
    }
  }, [
    shouldAutoSubmit,
    isAutoSubmittedByTimer,
    learnosityApp,
    isImpersonating,
    assignmentId,
  ]);

  return (
    <>
      {shouldAutoSubmit && (
        <AutoSubmitDialog
          data-test-id="auto-submit-dialog"
          data-cy="auto-submit-dialog"
          onClose={() => {
            learnosityExitPlayer(subject, router, state);
          }}
        />
      )}
      <div className="flex flex-1 justify-center bb-desktop:max-w-[130px]">
        {!isPreviewPlayer &&
          !(
            isNull(assignmentPlayer?.timer) ||
            isUndefined(assignmentPlayer?.timer)
          ) && (
            <Countdown
              quizTitle={quizTitle}
              userData={userData}
              assignmentPlayer={assignmentPlayer}
              params={params}
              assignmentId={assignmentId}
              timerInfo={timerInfo}
              checkTimeExtension={checkTimeExtension}
              data-test-id="bluebook-countdown-wrapper"
              data-cy="bluebook-countdown-wrapper"
            />
          )}
      </div>
    </>
  );
};

AssignmentSessionWrapper.propTypes = {
  userData: PropTypes.object,
  assignmentPlayer: PropTypes.object,
  params: PropTypes.object,
  assignmentId: PropTypes.string,
  isPreviewPlayer: PropTypes.bool,
  quizTitle: PropTypes.string,
};

AssignmentSessionWrapper.defaultProps = {
  userData: {},
  assignmentPlayer: {},
  params: {},
  assignmentId: '0',
  isPreviewPlayer: false,
  quizTitle: '',
};

export default compose(
  withAssignmentSubmit,
  withRouter,
  withSessionOnStudentAccountOnly,
)(AssignmentSessionWrapper);
